import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import LeadForm from "../components/LeadForm";
import PageHero from "../components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1800&q=80";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function ConsultoriasPage() {
  const { t } = useTranslation();
  const steps = t("consultorias.process.items", { returnObjects: true });
  const diffs = t("consultorias.differentiators.items", { returnObjects: true });

  return (
    <main>
      <PageHero
        eyebrow={t("consultorias.hero.eyebrow")}
        title={t("consultorias.hero.title")}
        subtitle={t("consultorias.hero.subtitle")}
        image={HERO_IMG}
        imageAlt="Sesión estratégica de consultoría empresarial"
      />

      {/* Timeline */}
      <section className="section-y">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-16 max-w-2xl">
            {t("consultorias.process.title")}
          </motion.h2>

          <div className="relative">
            {/* vertical line */}
            <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-border lg:-translate-x-1/2" />

            <div className="space-y-12">
              {steps.map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6 }}
                  className={`relative flex lg:items-center gap-6 ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>

                  {/* Number node */}
                  <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center ring-8 ring-background lg:absolute lg:left-1/2 lg:-translate-x-1/2">
                    {i + 1}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 ${i % 2 === 0 ? "lg:pr-20 lg:text-right" : "lg:pl-20"} lg:max-w-[45%]`}>
                    <div className="glass-card p-7">
                      <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{s.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="section-y bg-secondary/30 border-y border-border">
        <div className="container-page">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-12">{t("consultorias.differentiators.title")}</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {diffs.map((d, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="p-6 rounded-2xl border border-border bg-card/60">
                <CheckCircle2 className="w-7 h-7 text-primary mb-4" />
                <h3 className="font-semibold mb-2">{d.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-y">
        <div className="container-page max-w-3xl">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-bold mb-3 text-center">
            Solicita tu diagnóstico
          </motion.h2>
          <p className="text-center text-muted-foreground mb-10">
            30 minutos de llamada inicial. Sin compromiso, con valor desde el primer minuto.
          </p>
          <div className="glass-card p-8 md:p-10">
            <LeadForm pageOrigin="consultorias" defaultService="consultorias" />
          </div>
        </div>
      </section>
    </main>
  );
}
